import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import {
  PaziaFormButton,
  PaziaInput,
  PaziaLoader,
  PaziaFileDropzone,
  PaziaSwitch,
  PaziaTextarea,
  PaziaSelect,
} from "components/atoms";
import { useFooterSettings, useNewsletterSubscriptions, shopApi, FooterSettings } from "api/shop";
import { useToaster } from "utils";
import { useState, useEffect } from "react";
import { MdAdd, MdDelete } from "react-icons/md";
import { useShopImageUpload } from "../Sections/SectionForms/useShopImageUpload";

export const ShopFooterForm = () => {
  const { footerSettings, isLoading, invalidate } = useFooterSettings();
  const { showSuccess, showError } = useToaster();
  const { uploadImage } = useShopImageUpload();
  const [isSaving, setIsSaving] = useState(false);

  const methods = useForm<FormValues>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      [FORM_NAMES.LOGO]: footerSettings?.logo ?? "",
      [FORM_NAMES.BG_COLOR]: footerSettings?.bgColor ?? "#1e293b",
      [FORM_NAMES.TEXT_COLOR]: footerSettings?.textColor ?? "#ffffff",
      [FORM_NAMES.MAIN_TEXT]: footerSettings?.mainText ?? "",
      [FORM_NAMES.LOGO_SIZE]: footerSettings?.logoSize ?? "",
      [FORM_NAMES.FONT_FAMILY]: footerSettings?.fontFamily ?? "",
      [FORM_NAMES.FONT_SIZE]: footerSettings?.fontSize ?? "",
      [FORM_NAMES.SOCIALS]: footerSettings?.socials ?? [],
      [FORM_NAMES.TERMS]: footerSettings?.terms ?? [],
      [FORM_NAMES.NEWSLETTER_ENABLED]: footerSettings?.newsletter?.enabled ?? false,
      [FORM_NAMES.NEWSLETTER_TITLE]: footerSettings?.newsletter?.title ?? "Newsletter",
      [FORM_NAMES.NEWSLETTER_DESCRIPTION]: footerSettings?.newsletter?.description ?? "",
      [FORM_NAMES.NEWSLETTER_BUTTON_TEXT]: footerSettings?.newsletter?.buttonText ?? "Zapisz się",
      [FORM_NAMES.NEWSLETTER_SUCCESS_MESSAGE]: footerSettings?.newsletter?.successMessage ?? "Dziękujemy za zapisanie się!",
      [FORM_NAMES.NAV_COLUMNS]: footerSettings?.navColumns?.map(g => ({ title: g.title ?? "", links: g.links })) ?? [],
    },
  });
  const { handleSubmit, reset, control } = methods;

  const {
    fields: socialFields,
    append: appendSocial,
    remove: removeSocial,
  } = useFieldArray({
    control,
    name: FORM_NAMES.SOCIALS,
  });

  const {
    fields: termFields,
    append: appendTerm,
    remove: removeTerm,
  } = useFieldArray({
    control,
    name: FORM_NAMES.TERMS,
  });

  const {
    fields: navColumnFields,
    append: appendNavColumn,
    remove: removeNavColumn,
  } = useFieldArray({
    control,
    name: FORM_NAMES.NAV_COLUMNS,
  });

  // Reset form when footer settings load
  useEffect(() => {
    if (footerSettings) {
      reset({
        [FORM_NAMES.LOGO]: footerSettings.logo ?? "",
        [FORM_NAMES.BG_COLOR]: footerSettings.bgColor ?? "#1e293b",
        [FORM_NAMES.TEXT_COLOR]: footerSettings.textColor ?? "#ffffff",
        [FORM_NAMES.MAIN_TEXT]: footerSettings.mainText ?? "",
        [FORM_NAMES.LOGO_SIZE]: footerSettings.logoSize ?? "",
        [FORM_NAMES.FONT_FAMILY]: footerSettings.fontFamily ?? "",
        [FORM_NAMES.FONT_SIZE]: footerSettings.fontSize ?? "",
        [FORM_NAMES.SOCIALS]: footerSettings.socials ?? [],
        [FORM_NAMES.TERMS]: footerSettings.terms ?? [],
        [FORM_NAMES.NEWSLETTER_ENABLED]: footerSettings.newsletter?.enabled ?? false,
        [FORM_NAMES.NEWSLETTER_TITLE]: footerSettings.newsletter?.title ?? "Newsletter",
        [FORM_NAMES.NEWSLETTER_DESCRIPTION]: footerSettings.newsletter?.description ?? "",
        [FORM_NAMES.NEWSLETTER_BUTTON_TEXT]: footerSettings.newsletter?.buttonText ?? "Zapisz się",
        [FORM_NAMES.NEWSLETTER_SUCCESS_MESSAGE]: footerSettings.newsletter?.successMessage ?? "Dziękujemy za zapisanie się!",
        [FORM_NAMES.NAV_COLUMNS]: footerSettings.navColumns?.map(g => ({ title: g.title ?? "", links: g.links })) ?? [],
      });
    }
  }, [footerSettings, reset]);

  const onSubmit = async (data: FormValues) => {
    setIsSaving(true);
    try {
      // Upload logo if it's a file
      const logoUrl = data.logo ? await uploadImage(data.logo) : undefined;

      // Upload social icons if they are files
      const uploadedSocials = await Promise.all(
        data.socials.map(async (social) => ({
          image: await uploadImage(social.image),
          bgColor: social.bgColor,
          link: social.link,
        })),
      );

      const formattedData: FooterSettings = {
        logo: logoUrl,
        bgColor: data.bgColor || undefined,
        textColor: data.textColor || undefined,
        mainText: data.mainText || undefined,
        logoSize: data.logoSize === "" ? undefined : Number(data.logoSize),
        fontFamily: data.fontFamily || undefined,
        fontSize: data.fontSize === "" ? undefined : Number(data.fontSize),
        socials: uploadedSocials.length > 0 ? uploadedSocials : undefined,
        terms: data.terms.length > 0 ? data.terms : undefined,
        navColumns: data.navColumns && data.navColumns.length > 0
          ? data.navColumns.map(g => ({ title: g.title || undefined, links: g.links }))
          : undefined,
        newsletter: {
          enabled: data.newsletterEnabled,
          title: data.newsletterTitle,
          description: data.newsletterDescription || undefined,
          buttonText: data.newsletterButtonText || undefined,
          successMessage: data.newsletterSuccessMessage || undefined,
        },
      };

      await shopApi.updateFooterSettings(formattedData);
      showSuccess("Zapisano ustawienia stopki");
      invalidate();
    } catch (error) {
      showError("Nie udało się zapisać ustawień stopki");
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };

  const addSocial = () => {
    appendSocial({ image: "", bgColor: "#3b82f6", link: "" });
  };

  const addTerm = () => {
    appendTerm({ name: "", link: "" });
  };

  const addNavColumn = () => {
    appendNavColumn({ title: "", links: [] });
  };

  if (isLoading) return <PaziaLoader position="center" />;

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} className="grid max-w-3xl gap-6">
        {/* Basic Settings */}
        <div className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
          <h3 className="mb-4 text-lg font-medium text-slate-800 dark:text-slate-200">Podstawowe ustawienia</h3>
          <div className="grid gap-4">
            <div className="[&_img]:max-h-24">
              {/* <PaziaFileDropzone
                name={FORM_NAMES.LOGO}
                label="Logo"
                type="image"
                single
                preview="image"
              /> */}
            </div>
            <div className="grid gap-4 md:grid-cols-3">
              <PaziaInput name={FORM_NAMES.BG_COLOR} label="Kolor tła (hex)" placeholder="#1e293b" />
              <PaziaInput name={FORM_NAMES.TEXT_COLOR} label="Kolor tekstu (hex)" placeholder="#ffffff" />
              <PaziaInput name={FORM_NAMES.LOGO_SIZE} label="Wysokość logo (px)" placeholder="80" />
            </div>
            <div>
              <PaziaTextarea
                name={FORM_NAMES.MAIN_TEXT}
                label="Tekst główny"
                placeholder="Twój tekst w stopce"
                rows={3}
              />
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                Możesz łamać tekst nową linią (Enter).
              </p>
            </div>
          </div>
        </div>

        {/* Typography Settings */}
        <div className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
          <h3 className="mb-4 text-lg font-medium text-slate-800 dark:text-slate-200">Typografia</h3>
          <div className="grid gap-4 md:grid-cols-2">
            <PaziaSelect
              name={FORM_NAMES.FONT_FAMILY}
              label="Rodzaj fontu"
              options={[
                { value: "", label: "Domyślny (z motywu)" },
                { value: "Inter, sans-serif", label: "Inter" },
                { value: "Poppins, sans-serif", label: "Poppins" },
                { value: "'Playfair Display', serif", label: "Playfair Display" },
                { value: "system-ui, sans-serif", label: "Systemowy bezszeryfowy" },
                { value: "Georgia, serif", label: "Georgia (szeryfowy)" },
                { value: "'Courier New', monospace", label: "Monospace" },
              ]}
            />
            <PaziaInput name={FORM_NAMES.FONT_SIZE} label="Rozmiar fontu (px)" placeholder="16" />
          </div>
        </div>

        {/* Socials Section */}
        <div className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-lg font-medium text-slate-800 dark:text-slate-200">Social media</h3>
            <button
              type="button"
              onClick={addSocial}
              className="flex items-center gap-1 rounded-lg bg-indigo-100 px-3 py-1.5 text-sm font-medium text-indigo-600 transition-colors hover:bg-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-400 dark:hover:bg-indigo-900/50"
            >
              <MdAdd size={18} />
              Dodaj social
            </button>
          </div>

          {socialFields.length === 0 ? (
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Brak mediów społecznościowych. Kliknij "Dodaj social", aby dodać.
            </p>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {socialFields.map((field, index) => (
                <div
                  key={field.id}
                  className="rounded-lg border border-slate-200 bg-slate-50 p-4 dark:border-slate-600 dark:bg-slate-700"
                >
                  <div className="mb-3 flex items-center justify-between">
                    <span className="text-sm font-medium text-slate-600 dark:text-slate-400">Social #{index + 1}</span>
                    <button
                      type="button"
                      onClick={() => removeSocial(index)}
                      className="rounded p-1.5 text-slate-500 transition-colors hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-900/30 dark:hover:text-red-400"
                      title="Usuń social"
                    >
                      <MdDelete size={18} />
                    </button>
                  </div>
                  <div className="space-y-3">
                    <div className="[&_img]:max-h-16">
                      <PaziaFileDropzone
                        name={`${FORM_NAMES.SOCIALS}.${index}.image`}
                        label="Ikona"
                        type="image"
                        single
                        preview="image"
                      />
                    </div>
                    <PaziaInput
                      name={`${FORM_NAMES.SOCIALS}.${index}.bgColor`}
                      label="Kolor tła ikony (hex)"
                      placeholder="#3b82f6"
                    />
                    <PaziaInput
                      name={`${FORM_NAMES.SOCIALS}.${index}.link`}
                      label="Link"
                      placeholder="https://facebook.com/..."
                      required
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Terms Section */}
        <div className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-lg font-medium text-slate-800 dark:text-slate-200">Linki regulaminów</h3>
            <button
              type="button"
              onClick={addTerm}
              className="flex items-center gap-1 rounded-lg bg-indigo-100 px-3 py-1.5 text-sm font-medium text-indigo-600 transition-colors hover:bg-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-400 dark:hover:bg-indigo-900/50"
            >
              <MdAdd size={18} />
              Dodaj link
            </button>
          </div>

          {termFields.length === 0 ? (
            <p className="text-sm text-slate-500 dark:text-slate-400">Brak linków. Kliknij "Dodaj link", aby dodać.</p>
          ) : (
            <div className="space-y-3">
              {termFields.map((field, index) => (
                <div
                  key={field.id}
                  className="flex items-end gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-600 dark:bg-slate-700"
                >
                  <div className="flex-1">
                    <PaziaInput
                      name={`${FORM_NAMES.TERMS}.${index}.name`}
                      label="Nazwa"
                      placeholder="Regulamin serwisu"
                      required
                    />
                  </div>
                  <div className="flex-1">
                    <PaziaInput
                      name={`${FORM_NAMES.TERMS}.${index}.link`}
                      label="Link"
                      placeholder="/regulamin"
                      required
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeTerm(index)}
                    className="mb-1 rounded p-1.5 text-slate-500 transition-colors hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-900/30 dark:hover:text-red-400"
                    title="Usuń link"
                  >
                    <MdDelete size={18} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Nav Columns Section */}
        <div className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-lg font-medium text-slate-800 dark:text-slate-200">Kolumny nawigacyjne</h3>
            <button
              type="button"
              onClick={addNavColumn}
              className="flex items-center gap-1 rounded-lg bg-indigo-100 px-3 py-1.5 text-sm font-medium text-indigo-600 transition-colors hover:bg-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-400 dark:hover:bg-indigo-900/50"
            >
              <MdAdd size={18} />
              Dodaj kolumnę
            </button>
          </div>
          {navColumnFields.length === 0 ? (
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Brak kolumn nawigacyjnych. Kliknij "Dodaj kolumnę", aby dodać sekcję z linkami.
            </p>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {navColumnFields.map((field, index) => (
                <NavColumnEditor
                  key={field.id}
                  groupIndex={index}
                  control={control}
                  remove={removeNavColumn}
                />
              ))}
            </div>
          )}
        </div>

        {/* Newsletter Section */}
        <div className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
          <h3 className="mb-4 text-lg font-medium text-slate-800 dark:text-slate-200">Newsletter</h3>
          <div className="grid gap-4">
            <PaziaSwitch name={FORM_NAMES.NEWSLETTER_ENABLED} label="Włącz newsletter w stopce" />
            <PaziaInput name={FORM_NAMES.NEWSLETTER_TITLE} label="Tytuł" placeholder="Newsletter" />
            <div>
              <PaziaTextarea
                name={FORM_NAMES.NEWSLETTER_DESCRIPTION}
                label="Opis"
                placeholder="Zapisz się do naszego newslettera..."
                rows={2}
              />
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                Możesz łamać tekst nową linią (Enter).
              </p>
            </div>
            <PaziaInput name={FORM_NAMES.NEWSLETTER_BUTTON_TEXT} label="Tekst przycisku" placeholder="Zapisz się" />
            <PaziaInput name={FORM_NAMES.NEWSLETTER_SUCCESS_MESSAGE} label="Komunikat po zapisie" placeholder="Dziękujemy za zapisanie się!" />
          </div>
        </div>

        {/* Newsletter Subscribers */}
        <NewsletterSubscribersSection />

        <div className="flex justify-start">
          <PaziaFormButton disabled={isSaving} />
        </div>
      </form>
    </FormProvider>
  );
};

const FORM_NAMES = {
  LOGO: "logo",
  BG_COLOR: "bgColor",
  TEXT_COLOR: "textColor",
  MAIN_TEXT: "mainText",
  LOGO_SIZE: "logoSize",
  FONT_FAMILY: "fontFamily",
  FONT_SIZE: "fontSize",
  SOCIALS: "socials",
  TERMS: "terms",
  NEWSLETTER_ENABLED: "newsletterEnabled",
  NEWSLETTER_TITLE: "newsletterTitle",
  NEWSLETTER_DESCRIPTION: "newsletterDescription",
  NEWSLETTER_BUTTON_TEXT: "newsletterButtonText",
  NEWSLETTER_SUCCESS_MESSAGE: "newsletterSuccessMessage",
  NAV_COLUMNS: "navColumns",
} as const;

interface NavColumnEditorProps {
  groupIndex: number;
  control: any;
  remove: (index: number) => void;
}

const NavColumnEditor = ({ groupIndex, control, remove }: NavColumnEditorProps) => {
  const { fields: linkFields, append: appendLink, remove: removeLink } = useFieldArray({
    control,
    name: `${FORM_NAMES.NAV_COLUMNS}.${groupIndex}.links` as any,
  });

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 dark:border-slate-600 dark:bg-slate-700">
      <div className="mb-3 flex items-center justify-between">
        <span className="text-sm font-medium text-slate-600 dark:text-slate-400">Kolumna #{groupIndex + 1}</span>
        <button
          type="button"
          onClick={() => remove(groupIndex)}
          className="rounded p-1.5 text-slate-500 transition-colors hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-900/30 dark:hover:text-red-400"
          title="Usuń kolumnę"
        >
          <MdDelete size={18} />
        </button>
      </div>
      <div className="space-y-3">
        <PaziaInput
          name={`${FORM_NAMES.NAV_COLUMNS}.${groupIndex}.title`}
          label="Tytuł kolumny (opcjonalny)"
          placeholder="np. Oferta, Firma, Pomoc"
        />
        <div className="space-y-2">
          {linkFields.map((linkField, linkIndex) => (
            <div key={linkField.id} className="flex items-end gap-2 rounded border border-slate-200 bg-white p-2 dark:border-slate-500 dark:bg-slate-600">
              <div className="flex-1">
                <PaziaInput
                  name={`${FORM_NAMES.NAV_COLUMNS}.${groupIndex}.links.${linkIndex}.name`}
                  label="Nazwa"
                  placeholder="np. O nas"
                  required
                />
              </div>
              <div className="flex-1">
                <PaziaInput
                  name={`${FORM_NAMES.NAV_COLUMNS}.${groupIndex}.links.${linkIndex}.link`}
                  label="Link"
                  placeholder="/o-nas"
                  required
                />
              </div>
              <button
                type="button"
                onClick={() => removeLink(linkIndex)}
                className="mb-1 rounded p-1.5 text-slate-500 transition-colors hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-900/30 dark:hover:text-red-400"
                title="Usuń link"
              >
                <MdDelete size={18} />
              </button>
            </div>
          ))}
        </div>
        <button
          type="button"
          onClick={() => appendLink({ name: "", link: "" })}
          className="flex items-center gap-1 rounded bg-slate-200 px-2 py-1 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-300 dark:bg-slate-600 dark:text-slate-300 dark:hover:bg-slate-500"
        >
          <MdAdd size={14} />
          Dodaj link
        </button>
      </div>
    </div>
  );
};

const socialSchema = yup.object({
  image: yup.mixed().required("Ikona jest wymagana"),
  bgColor: yup.string().default("#3b82f6"),
  link: yup.string().required("Link jest wymagany"),
});

const termSchema = yup.object({
  name: yup.string().required("Nazwa jest wymagana"),
  link: yup.string().required("Link jest wymagany"),
});

const schema = yup
  .object({
    [FORM_NAMES.LOGO]: yup.mixed(),
    [FORM_NAMES.BG_COLOR]: yup.string().default("#1e293b"),
    [FORM_NAMES.TEXT_COLOR]: yup.string().default("#ffffff"),
    [FORM_NAMES.MAIN_TEXT]: yup.string().default(""),
    [FORM_NAMES.LOGO_SIZE]: yup.number().nullable().transform((v, o) => (o === "" ? null : v)).min(20).max(400),
    [FORM_NAMES.FONT_FAMILY]: yup.string().default(""),
    [FORM_NAMES.FONT_SIZE]: yup.number().nullable().transform((v, o) => (o === "" ? null : v)).min(10).max(32),
    [FORM_NAMES.SOCIALS]: yup.array().of(socialSchema),
    [FORM_NAMES.TERMS]: yup.array().of(termSchema),
    [FORM_NAMES.NEWSLETTER_ENABLED]: yup.boolean().default(false),
    [FORM_NAMES.NEWSLETTER_TITLE]: yup.string().default("Newsletter"),
    [FORM_NAMES.NEWSLETTER_DESCRIPTION]: yup.string().default(""),
    [FORM_NAMES.NEWSLETTER_BUTTON_TEXT]: yup.string().default("Zapisz się"),
    [FORM_NAMES.NEWSLETTER_SUCCESS_MESSAGE]: yup.string().default("Dziękujemy za zapisanie się!"),
    [FORM_NAMES.NAV_COLUMNS]: yup.array().of(
      yup.object({
        title: yup.string().default(""),
        links: yup.array().of(
          yup.object({
            name: yup.string().required("Nazwa jest wymagana"),
            link: yup.string().required("Link jest wymagany"),
          })
        ),
      })
    ),
  })
  .required();

interface FormValues {
  logo: string | File;
  bgColor: string;
  textColor: string;
  mainText: string;
  logoSize: number | "";
  fontFamily: string;
  fontSize: number | "";
  socials: Array<{ image: string | File; bgColor: string; link: string }>;
  terms: Array<{ name: string; link: string }>;
  newsletterEnabled: boolean;
  newsletterTitle: string;
  newsletterDescription: string;
  newsletterButtonText: string;
  newsletterSuccessMessage: string;
  navColumns: Array<{ title: string; links: Array<{ name: string; link: string }> }>;
}

const NewsletterSubscribersSection = () => {
  const { subscriptions, total, isLoading, invalidate } = useNewsletterSubscriptions();
  const { showSuccess, showError } = useToaster();

  const handleDelete = async (id: number) => {
    try {
      await shopApi.deleteNewsletterSubscription(id);
      showSuccess("Usunięto subskrybenta");
      invalidate();
    } catch {
      showError("Nie udało się usunąć subskrybenta");
    }
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
      <h3 className="mb-4 text-lg font-medium text-slate-800 dark:text-slate-200">
        Subskrybenci newslettera ({total})
      </h3>
      {isLoading ? (
        <PaziaLoader position="center" />
      ) : subscriptions.length === 0 ? (
        <p className="text-sm text-slate-500 dark:text-slate-400">Brak subskrybentów.</p>
      ) : (
        <div className="space-y-2">
          {subscriptions.map((sub) => (
            <div
              key={sub.id}
              className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-4 py-2 dark:border-slate-600 dark:bg-slate-700"
            >
              <div>
                <span className="text-sm font-medium text-slate-800 dark:text-slate-200">{sub.email}</span>
                <span className="ml-3 text-xs text-slate-500 dark:text-slate-400">
                  {new Date(sub.createdDate).toLocaleDateString("pl-PL")}
                </span>
              </div>
              <button
                type="button"
                onClick={() => handleDelete(sub.id)}
                className="rounded p-1.5 text-slate-500 transition-colors hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-900/30 dark:hover:text-red-400"
                title="Usuń"
              >
                <MdDelete size={18} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
